import {
  createUserWithEmailAndPassword,
  getMultiFactorResolver,
  GoogleAuthProvider,
  inMemoryPersistence,
  initializeAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  TotpMultiFactorGenerator,
  updateProfile,
  type Auth,
  type MultiFactorResolver,
  type User,
} from 'firebase/auth'
import { FirebaseError, getApp, getApps, initializeApp } from 'firebase/app'
import type { PublicConfig } from './types'
import { postJSON } from './api'

export type AuthOutcome =
  | { status:'complete' }
  | { status:'verification_sent' }
  | { status:'mfa_challenge' }

let firebaseAuth:Auth|undefined
let pendingResolver:MultiFactorResolver|undefined

function authFor(config:PublicConfig) {
  if (firebaseAuth) return firebaseAuth
  const app = getApps().length ? getApp() : initializeApp({
    apiKey:config.firebase.apiKey,
    authDomain:config.firebase.authDomain,
    projectId:config.firebase.projectId,
  })
  firebaseAuth = initializeAuth(app, { persistence: inMemoryPersistence })
  return firebaseAuth
}

async function exchange(auth:Auth):Promise<AuthOutcome> {
  const user = auth.currentUser
  if (!user) throw new Error('Sign-in did not return a user.')
  const idToken = await user.getIdToken(true)
  await postJSON('/api/auth/session',{idToken})
  await signOut(auth)
  return {status:'complete'}
}

function captureMFA(error:unknown, auth:Auth):AuthOutcome|undefined {
  if (error instanceof FirebaseError && error.code === 'auth/multi-factor-auth-required') {
    pendingResolver = getMultiFactorResolver(auth,error as Parameters<typeof getMultiFactorResolver>[1])
    return {status:'mfa_challenge'}
  }
}

export async function loginWithEmail(config:PublicConfig,email:string,password:string):Promise<AuthOutcome> {
  const auth = authFor(config)
  try {
    const result = await signInWithEmailAndPassword(auth,email,password)
    if (!result.user.emailVerified) {
      try {
        await requestVerificationEmail(result.user)
      } finally {
        await signOut(auth)
      }
      throw new Error('Verify your email first. We sent you a new verification link.')
    }
  } catch (error) {
    const outcome = captureMFA(error,auth)
    if (outcome) return outcome
    throw error
  }
  return exchange(auth)
}

export async function registerWithEmail(config:PublicConfig,name:string,email:string,password:string):Promise<AuthOutcome> {
  const auth = authFor(config)
  const result = await createUserWithEmailAndPassword(auth,email,password)
  await updateProfile(result.user,{displayName:name})
  try {
    await requestVerificationEmail(result.user)
  } finally {
    await signOut(auth)
  }
  return {status:'verification_sent'}
}

export async function loginWithGoogle(config:PublicConfig):Promise<AuthOutcome> {
  const auth = authFor(config)
  try {
    await signInWithPopup(auth,new GoogleAuthProvider())
  } catch (error) {
    const outcome = captureMFA(error,auth)
    if (outcome) return outcome
    throw error
  }
  return exchange(auth)
}

export async function completeTotpSignIn(config:PublicConfig,code:string):Promise<AuthOutcome> {
  if (!pendingResolver) throw new Error('Your sign-in challenge expired. Start again.')
  const hint = pendingResolver.hints.find(value=>value.factorId===TotpMultiFactorGenerator.FACTOR_ID)
  if (!hint) throw new Error('This account does not have a supported authenticator factor.')
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid,code)
  const auth = authFor(config)
  await pendingResolver.resolveSignIn(assertion)
  pendingResolver = undefined
  return exchange(auth)
}

async function requestVerificationEmail(user:User) {
  const idToken = await user.getIdToken(true)
  await postJSON('/api/auth/verification-email',{idToken})
}

export async function requestPasswordReset(email:string) {
  if (!email) throw new Error('Enter your email address first.')
  await postJSON('/api/auth/password-reset',{email})
}

export function authForEmailAction(config:PublicConfig) {
  return authFor(config)
}
